import { useAuth } from '@clerk/react'
import { BookOpen, ChevronDown, List, Plus, Star } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Link, useLocation } from '#router'
import { useProblemListsOptional } from '@/components/problem-lists/problem-list-context'
import { TopicIconSmall } from '@/components/problems/topic-icon-small'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

export function MobileListsDrawer() {
  const { isSignedIn } = useAuth()
  const listCtx = useProblemListsOptional()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const { data: bookChapters } = trpc.problems.getBookChapters.useQuery()
  const [chaptersCollapsed, setChaptersCollapsed] = useState(true)

  // Swipe-to-close gesture tracking
  const touchStartX = useRef(0)
  const touchCurrentX = useRef(0)

  const { navigate } = location

  const handleCreateList = useCallback(async () => {
    const trimmed = newListName.trim()
    if (!trimmed || !listCtx?.createList) return

    setIsCreating(true)
    setCreateError(null)

    try {
      const result = await listCtx.createList(trimmed)
      if (result) {
        setNewListName('')
        setCreateError(null)
        setShowCreateDialog(false)
        setOpen(false)
        navigate(`/problems/lists/${result.id}`)
      } else {
        setCreateError('A list with that name already exists')
      }
    } finally {
      setIsCreating(false)
    }
  }, [newListName, listCtx, navigate])

  const handleDialogOpenChange = useCallback((dialogOpen: boolean) => {
    setShowCreateDialog(dialogOpen)
    if (!dialogOpen) {
      setNewListName('')
      setCreateError(null)
      setIsCreating(false)
    }
  }, [])

  // Swipe-left to close the sheet
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchCurrentX.current = e.touches[0].clientX
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchCurrentX.current = e.touches[0].clientX
  }, [])

  const handleTouchEnd = useCallback(() => {
    const delta = touchStartX.current - touchCurrentX.current
    // Swipe left by at least 80px to close
    if (delta > 80) {
      setOpen(false)
    }
  }, [])

  if (!isSignedIn) return null

  const lists = listCtx?.lists ?? []
  const activeListId = location.pathname.match(
    /^\/problems\/lists\/([^/]+)/,
  )?.[1]

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-[44px] min-w-[44px]"
            aria-label="Saved"
          >
            <List className="h-4 w-4" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-[280px] p-0"
          showCloseButton={false}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <SheetHeader className="flex-row items-center justify-between border-b px-4 py-3">
            <SheetTitle className="text-base">Saved</SheetTitle>
            <button
              type="button"
              onClick={() => setShowCreateDialog(true)}
              className="flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-accent"
              aria-label="Create new list"
            >
              <Plus className="h-4 w-4" />
            </button>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            <nav className="p-2" aria-label="Problem lists">
              {lists.map((list) => {
                const isActive = activeListId === list.id
                return (
                  <Link
                    key={list.id}
                    href={`/problems/lists/${list.id}`}
                    onClick={() => setOpen(false)}
                    className={cn(
                      'flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent',
                      isActive && 'bg-accent font-medium',
                    )}
                  >
                    {list.isSystem ? (
                      <Star className="h-4 w-4 shrink-0 fill-yellow-400 text-yellow-400" />
                    ) : list.sourceType === 'chapter' ? (
                      <BookOpen className="h-4 w-4 shrink-0 text-[#51B0BC]" />
                    ) : list.sourceType === 'topic' ? (
                      <TopicIconSmall className="h-4 w-4 shrink-0" />
                    ) : (
                      <div className="bg-muted-foreground/20 flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px] font-bold">
                        {list.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="truncate">{list.name}</span>
                  </Link>
                )
              })}
            </nav>

            {/* Chapters section */}
            {bookChapters && bookChapters.length > 0 && (
              <div className="border-t">
                <button
                  type="button"
                  onClick={() => setChaptersCollapsed((v) => !v)}
                  className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium"
                >
                  Chapters
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform',
                      !chaptersCollapsed && 'rotate-180',
                    )}
                  />
                </button>
                {!chaptersCollapsed && (
                  <nav className="px-2 pb-2" aria-label="Book chapters">
                    {bookChapters.map((ch) => {
                      const isActive =
                        location.pathname === `/problems/book/${ch.slug}`
                      return (
                        <Link
                          key={ch.slug}
                          href={`/problems/book/${ch.slug}`}
                          onClick={() => setOpen(false)}
                          className={cn(
                            'flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent',
                            isActive && 'bg-accent font-medium',
                          )}
                        >
                          <BookOpen className="h-4 w-4 shrink-0 text-[#51B0BC]" />
                          <span className="truncate">{ch.title}</span>
                        </Link>
                      )
                    })}
                  </nav>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Create list dialog — positioned near top on mobile so keyboard doesn't cover it */}
      <Dialog open={showCreateDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="top-[20%] translate-y-0 sm:top-[50%] sm:max-w-sm sm:translate-y-[-50%]">
          <DialogHeader>
            <DialogTitle>Create New List</DialogTitle>
            <DialogDescription>
              Give your new list a name to get started.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <label
                htmlFor="mobile-new-list-name"
                className="mb-1 block text-sm font-medium"
              >
                Title
              </label>
              <div className="relative">
                <Input
                  id="mobile-new-list-name"
                  value={newListName}
                  onChange={(e) => {
                    if (e.target.value.length <= 30) {
                      setNewListName(e.target.value)
                      setCreateError(null)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleCreateList()
                    }
                  }}
                  placeholder="Enter a list name"
                  maxLength={30}
                  aria-invalid={!!createError}
                  autoFocus
                  className="pr-12"
                />
                <span className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2 text-xs">
                  {newListName.length}/30
                </span>
              </div>
              {createError && (
                <p className="text-destructive mt-1 text-xs">{createError}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleCreateList}
                disabled={!newListName.trim() || isCreating}
                className="w-full"
              >
                Create
              </Button>
              <Button
                variant="ghost"
                onClick={() => handleDialogOpenChange(false)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
